import { useMemo } from "react";
import { useSelector } from "react-redux";
import { ROUTES } from "@lhd/constants";
import { user } from "@lhd/features/users/selector";
import { getCompanyId } from "@lhd/utility";
import { Company } from "@lhd/types";
import { useFetchAppraisalPermission } from "./appPermission";
import { useGetAuth } from "./useGetAuth";

/**
 * Hook to check if user has permission to access a specific route
 * Returns true if user has permission, false otherwise
 */
export const useRoutePermission = (route: string): boolean => {
  const { auth, isAdmin, canChangeUser, canChangeOrgChart, isUserResigned } =
    useGetAuth();

  const currentCompanyLogin = auth?.companies?.find(
    (i: Company) => Number(i?.id) === Number(getCompanyId()),
  );
  const isRootCompany = currentCompanyLogin?.is_root_company;
  const hasCompanyAdminContext =
    isAdmin && (currentCompanyLogin?.is_admin || isRootCompany);

  const { data: appraisalPermissionData } = useFetchAppraisalPermission();
  const hasAppraisalPermission =
    appraisalPermissionData?.general_appraisal_delete || auth?.is_admin;

  const hasPermission = useMemo(() => {
    // Helper function to check if route matches
    const routeMatches = (routeToCheck: string) => {
      const normalizedRoute = route.replace(/^\//, "");
      const normalizedRouteToCheck = routeToCheck.replace(/^\//, "");
      const matches =
        normalizedRoute === normalizedRouteToCheck ||
        route.includes(routeToCheck) ||
        routeToCheck.includes(normalizedRoute);
      return matches;
    };

    if (
      routeMatches(ROUTES.BUSINESS_RPG) ||
      routeMatches(ROUTES.BUSINESS_RPG_PLAY)
    ) {
      return true;
    }

    // Organization Chart route - requires canChangeUser OR can_change_org_chart
    if (routeMatches(ROUTES.ORGANIZATION_CHART)) {
      return canChangeUser || canChangeOrgChart;
    }

    // Routes that require canChangeUser permission
    if (
      routeMatches(ROUTES.PERSONNEL) ||
      routeMatches(ROUTES.CUSTOMER_LIST) ||
      routeMatches(ROUTES.PROJECT_MANAGE) ||
      routeMatches(ROUTES.POSITIONS)
    ) {
      return canChangeUser;
    }

    // Routes that require admin permission
    if (routeMatches(ROUTES.ORGANIZATION_MANAGEMENT)) {
      return !!auth?.is_admin;
    }

    // Routes that require company admin context
    if (
      routeMatches(ROUTES.COMPANIES) ||
      routeMatches(ROUTES.APP_CATEGORIES) ||
      routeMatches(ROUTES.APP_SUBMITTED_MANAGEMENT)
    ) {
      return hasCompanyAdminContext;
    }

    // Routes that require admin and root company
    if (
      routeMatches(ROUTES.ADMIN_CONFIG) ||
      routeMatches(ROUTES.MAINTENANCE_NOTIFICATION)
    ) {
      return isAdmin && isRootCompany;
    }

    // Appraisal routes
    if (
      routeMatches(ROUTES.APPRAISAL_LIST) ||
      routeMatches(ROUTES.APPRAISAL_KGI_LIST) ||
      routeMatches(ROUTES.APPRAISAL_SETTING)
    ) {
      return hasAppraisalPermission;
    }

    if (
      routeMatches(`${ROUTES.WORKERS}/${ROUTES.CREATE}`) ||
      routeMatches("workers/create")
    ) {
      // For create/edit actions, need canChangeUser
      return canChangeUser && !isUserResigned;
    }
    // For viewing workers list, everyone can access
    if (routeMatches(ROUTES.WORKERS)) {
      return true;
    }

    // Worker sheets route - requires canChangeUser permission
    // Note: ROUTES.WORKER_SHEETS is "worker_sheets" (relative to workers route)
    if (routeMatches(ROUTES.WORKER_SHEETS) || route === ROUTES.WORKER_SHEETS) {
      return canChangeUser && !isUserResigned;
    }

    // Default: allow access (some routes don't have permission checks in sidebar)
    return true;
  }, [
    route,
    auth,
    isAdmin,
    canChangeUser,
    canChangeOrgChart,
    hasCompanyAdminContext,
    isRootCompany,
    hasAppraisalPermission,
    isUserResigned,
  ]);

  return hasPermission;
};

/**
 * Hook to check if user has permission to perform create/edit actions
 */
export const useCanEditWorker = (): boolean => {
  const { canChangeUser, isUserResigned } = useGetAuth();

  return canChangeUser && !isUserResigned;
};
